import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations, SUPPORTED_LANGS, DEFAULT_LANG } from "./translations";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  t: (key) => key,
  switchLang: () => {},
});

function langFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  if (seg && SUPPORTED_LANGS.includes(seg)) return seg;
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const lang = langFromPath(pathname);

  const t = useCallback(
    (key) => {
      const dict = translations[lang] || translations[DEFAULT_LANG];
      return dict[key] ?? translations[DEFAULT_LANG][key] ?? key;
    },
    [lang],
  );

  const switchLang = useCallback(
    (next) => {
      if (!SUPPORTED_LANGS.includes(next)) return;
      const segs = pathname.split("/").filter(Boolean);
      const first = segs[0];
      const rest = SUPPORTED_LANGS.includes(first) ? segs.slice(1) : segs;
      const restPath = rest.join("/");
      const target =
        next === DEFAULT_LANG
          ? "/" + restPath
          : "/" + next + (restPath ? "/" + restPath : "");
      navigate(target.replace(/\/$/, "") || "/");
    },
    [pathname, navigate],
  );

  const value = useMemo(() => ({ lang, t, switchLang }), [lang, t, switchLang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  return useContext(LanguageContext);
}
